'use client'

import Link from 'next/link'
import { Button } from '@/shared/ui/button/button'
import { ThemeToggle } from '@/shared/ui/theme-toggle/theme-toggle'
import { Copy, Phone, Check } from 'lucide-react'
import { useState } from 'react'
import { Tooltip } from '@nextui-org/react'
import { MobileMenu } from '@/shared/ui/mobile-menu/mobile-menu'
import { useTheme } from 'next-themes'
import { useCompaniesCount } from '@/shared/lib/hooks/useCompaniesCount'

const PHONE_NUMBER = '+79254040999'

export const Header = () => {
  const [copied, setCopied] = useState(false)
  const { theme } = useTheme()
  const { count, loading } = useCompaniesCount()

  const handleCopy = () => {
    navigator.clipboard.writeText(PHONE_NUMBER)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <header
      className="w-full"
      role="banner"
    >
      <div className="w-full max-w-[1400px] mx-auto flex h-14 items-center px-2 md:container md:px-4">
        <Link href="/" className="flex items-center space-x-2" aria-label="На главную">
          <div className="h-14 w-auto">
            <img
              src={theme === 'dark' ? '/images/pervaya-cena-logo-black.svg' : '/images/pervaya-cena-logo.svg'}
              alt="Первая-цена.рф"
              className="h-full w-auto transition-all duration-300"
            />
          </div>
        </Link>

        <div className="flex-1 flex justify-end">
          <div className="hidden md:flex items-center space-x-2">
            <Button
              variant="ghost"
              className="text-orange-500 hover:text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20"
              onClick={() => {
                const catalogSection = document.getElementById('companies-catalog')
                if (catalogSection) {
                  catalogSection.scrollIntoView({ behavior: 'smooth' })
                }
              }}
            >
              Сейчас в продаже{' '}
              {!loading && <span className="text-orange-500 font-semibold">({count || 0})</span>}
            </Button>
            <div className="flex items-center space-x-1 border-r pr-2 mr-2">
              <ThemeToggle />
            </div>
            <Link
              href="https://wa.me/79254040999"
              target="_blank"
              className="flex items-center"
              aria-label="Написать в WhatsApp"
            >
              <Button variant="ghost" size="icon">
                <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current" aria-hidden="true">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                </svg>
              </Button>
            </Link>
            <Link
              href="https://t.me/first_prc"
              target="_blank"
              className="flex items-center"
              aria-label="Написать в Telegram"
            >
              <Button variant="ghost" size="icon">
                <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current" aria-hidden="true">
                  <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
                </svg>
              </Button>
            </Link>
            <div className="relative">
              <Button
                variant="default"
                className="bg-orange-500 hover:bg-orange-600 text-white flex items-center gap-2 pr-8"
                asChild
              >
                <Link href={`tel:${PHONE_NUMBER}`} aria-label="Позвонить">
                  {PHONE_NUMBER}
                  <Phone className="h-4 w-4" aria-hidden="true" />
                </Link>
              </Button>
              <Tooltip content={copied ? 'Скопировано!' : 'Копировать номер'}>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1 h-4 w-4 p-1 bg-transparent hover:bg-transparent"
                  onClick={handleCopy}
                  aria-label={copied ? 'Номер скопирован' : 'Копировать номер телефона'}
                >
                  {copied ? (
                    <Check className="h-3 w-3" aria-hidden="true" />
                  ) : (
                    <Copy className="h-3 w-3" aria-hidden="true" />
                  )}
                </Button>
              </Tooltip>
            </div>
          </div>
          <div className="md:hidden flex items-center gap-2">
            <Button
              variant="default"
              size="icon"
              className="!p-0 bg-orange-500 hover:bg-orange-600 h-10 w-10"
              asChild
            >
              <Link href={`tel:${PHONE_NUMBER}`} aria-label="Позвонить">
                <Phone className="h-5 w-5" aria-hidden="true" />
              </Link>
            </Button>
            <MobileMenu phoneNumber={PHONE_NUMBER} />
          </div>
        </div>
      </div>
    </header>
  )
}
